import {memo, useCallback} from 'react';

import {capitalize} from 'lodash';
import {useTranslation} from 'react-i18next';

import {SplitBillResult} from '../interfaces';

import {SplitResultActions} from './SplitResultCardActions';
import {SplitResultDetails} from './SplitResultCardDetails';
import {SplitResultParticipants} from './SplitResultCardParticipants';

interface SplitResultCardProps {
  splitBill: SplitBillResult;
  onAddSplit: () => void;
  onUndo: () => void;
  isCreatingSplit?: boolean;
  splitCreateError?: boolean;
  isSplitAdded?: boolean;
}

export const SplitResultCard = memo(
  ({
    splitBill,
    onAddSplit,
    onUndo,
    isCreatingSplit,
    splitCreateError,
    isSplitAdded,
  }: SplitResultCardProps) => {
    const {t} = useTranslation();
    const {payer, participants, split, total, description} = splitBill;

    const isPayer = useCallback(
      (name: string) => name.toLowerCase() === payer.toLowerCase(),
      [payer],
    );

    const participantLabel = useCallback(
      (name: string): string => {
        const displayName = capitalize(name);
        if (isPayer(name)) {
          return `${displayName} (${t('split_bills_page.already_paid')})`;
        }
        return `${displayName} ${t('split_bills_page.owes_you')}`;
      },
      [isPayer, t],
    );

    return (
      <div className="split-result-card">
        <SplitResultParticipants
          participants={participants}
          split={split}
          isPayer={isPayer}
          participantLabel={participantLabel}
        />

        <div className="split-result-divider" />

        <SplitResultDetails
          total={total}
          payer={payer}
          description={description}
        />

        <SplitResultActions
          key={JSON.stringify(splitBill)}
          onUndo={onUndo}
          onAddSplit={onAddSplit}
          isCreatingSplit={isCreatingSplit}
          splitCreateError={splitCreateError}
          isSplitAdded={isSplitAdded}
        />
      </div>
    );
  },
);
